import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonBase from '~components/button-base';
import Typography from '~components/typography';
import { ArrowTriangle } from '~svg-components';
import hexToRGBA from '~utils/hex-to-rgba';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      opacity: 0,
      marginTop: 0,
      transition: '0.3s opacity, 0.3s margin-top ease',
      width: 280,
    },

    rootDelay: {
      opacity: 1,
      marginTop: -8,
    },

    nextRootDelay: {
      opacity: 1,
      marginTop: -8,
    },

    mainContent: {
      padding: 20,
      textAlign: 'center',
      backgroundColor: theme.colors.brandIcon,
      boxShadow: theme.shadow.componentShallow,
      borderRadius: theme.borderRadius.small,
    },

    featureImg: {
      marginTop: 4,
      width: 200,
      height: 48,
    },

    title: {
      marginTop: 10,
    },

    description: {
      marginTop: 4,
    },

    button: {
      width: 128,
      marginTop: 16,
      padding: '7px 0px',
      borderRadius: theme.borderRadius.small,
      border: `1px solid ${hexToRGBA(theme.colors.white, 0.5)}`,

      '&:hover': {
        backgroundColor: `${hexToRGBA(theme.colors.white, 0.1)}`,
      },
    },

    arrow: {
      textAlign: 'center',
      height: 11,

      '& svg': {
        transform: 'rotate(180deg)',
        fill: theme.colors.brandIcon,
      },
    },
  }),
  { name: 'Popper' }
);

function Popper({ hintConfig, attrs, handleRead, isLastHint }) {
  const classes = useStyles();
  const [delay, setDelay] = useState(false);
  const [startNextAnimation, setStartNextAnimation] = useState(false);
  const { t } = useTranslation('release-hint');

  useEffect(() => {
    const popperTimeId = setTimeout(() => {
      setDelay(true);
    }, 2000);
    return () => {
      clearTimeout(popperTimeId);
    };
  }, [delay]);

  const handleClickButton = () => {
    setDelay(false);
    setStartNextAnimation(false);

    setTimeout(() => {
      handleRead();
    }, 300);

    if (!isLastHint) {
      setTimeout(() => {
        setStartNextAnimation(true);
      }, 700);
    }
  };

  return (
    <div
      className={classNames(classes.root, {
        [classes.rootDelay]: delay && !startNextAnimation,
        [classes.nextRootDelay]: startNextAnimation,
      })}
      tabIndex="-1"
      {...attrs}
    >
      <div className={classes.arrow}>
        <ArrowTriangle />
      </div>
      <div className={classes.mainContent}>
        <img className={classes.featureImg} src={hintConfig.pic} alt="expired" />
        <Typography variant="h5" themeColor="white" className={classes.title}>
          {hintConfig.title}
        </Typography>
        <Typography variant="body2" themeColor="white" className={classes.description}>
          {hintConfig.description}
        </Typography>
        <ButtonBase className={classes.button} onClick={handleClickButton}>
          <Typography variant="h6" themeColor="white">
            {isLastHint ? t('got_it') : t('next')}
          </Typography>
        </ButtonBase>
      </div>
    </div>
  );
}

Popper.propTypes = {
  hintConfig: PropTypes.object,
  attrs: PropTypes.any,
  handleRead: PropTypes.func,
  isLastHint: PropTypes.bool,
};

export default Popper;
